<template>
  <div class="phone-frame">
  <div class="admin-shell">
    <div class="admin-toast" :class="{ show: toastMsg }" @click="toastMsg = ''">{{ toastMsg }}</div>

    <!-- Top bar -->
    <header class="admin-topbar">
      <h1 class="atb-logo">⚙️ 管理后台</h1>
      <div class="atb-right">
        <span class="atb-user">{{ userName }}</span>
        <button class="atb-logout" @click="goHome">← 回首页</button>
      </div>
    </header>

    <div class="admin-body">
      <!-- Sidebar -->
      <aside class="admin-sidebar">
        <button :class="{ active: tab === 'applications' }" @click="tab = 'applications'">📋 带领人审批</button>
        <button :class="{ active: tab === 'leaders' }" @click="tab = 'leaders'">🌟 带领人管理</button>
        <button :class="{ active: tab === 'recruit' }" @click="tab = 'recruit'">📣 招募配置</button>
        <button :class="{ active: tab === 'window' }" @click="tab = 'window'">🪟 窗口配置</button>
        <button :class="{ active: tab === 'users' }" @click="tab = 'users'">👤 用户管理</button>
        <button :class="{ active: tab === 'groups' }" @click="tab = 'groups'; loadGroups()">📋 小组管理</button>
        <button :class="{ active: tab === 'checklist' }" @click="tab = 'checklist'; loadChecklistStatus()">📋 清单管理</button>
      </aside>

      <!-- Main content -->
      <main class="admin-main">
        <!-- ═══════════════ Applications ═══════════════ -->
        <section v-if="tab === 'applications'">
          <h2>📋 带领人审批</h2>

          <!-- Whitelist panel -->
          <div class="wl-toggle" @click="wlOpen = !wlOpen">
            📋 答辩白名单（{{ qualifiedLeaders.length }}人）<span style="font-size:11px;color:#999">{{ wlOpen ? '▲ 收起' : '▼ 展开' }}</span>
          </div>
          <div v-if="wlOpen" class="wl-panel">
            <div class="wl-hint">选择认证模块，从飞书表格复制学员ID列粘贴到下方批量添加</div>
            <div class="wl-row">
              <select v-model="wlStage" class="wl-select">
                <option value="">-- 选择模块 --</option>
                <option v-for="s in wlStages" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <textarea v-model="wlInput" placeholder="粘贴学员ID，每行一个或用逗号分隔&#10;例如：&#10;156&#10;181&#10;291" rows="4" class="wl-textarea" :disabled="!wlStage"></textarea>
            <button class="btn btn-approve" @click="doAddQualified" :disabled="!wlInput.trim() || !wlStage">➕ 批量添加</button>
            <div v-if="wlGrouped['未分类'] && wlGrouped['未分类'].length > 0" style="margin-bottom:6px;">
              <button class="btn-small-outline" @click="doMigrateQualified">📦 迁移旧数据到「微习惯设计深化」</button>
            </div>
            <div v-if="qualifiedLeaders.length > 0" style="margin-top:8px;">
              <div v-for="(ids, stage) in wlGrouped" :key="stage" style="margin-bottom:6px;">
                <div style="font-size:12px;font-weight:600;color:#666;margin-bottom:4px;">{{ stage }}（{{ ids.length }}人）</div>
                <div class="wl-list">
                  <span v-for="q in ids" :key="q.miniUserId" class="wl-chip">
                    {{ q.miniUserId }}
                    <button class="wl-remove" @click="doRemoveQualified(q.miniUserId, q.certStage)" title="移除">×</button>
                  </span>
                </div>
              </div>
            </div>
            <div v-else style="color:#999;font-size:12px;">暂无白名单</div>
          </div>

          <div class="filter-row">
            <span class="chip" :class="{ on: appFilter === 'pending' }" @click="appFilter = 'pending'">待审核</span>
            <span class="chip" :class="{ on: appFilter === 'approved' }" @click="appFilter = 'approved'">已通过</span>
            <span class="chip" :class="{ on: appFilter === 'rejected' }" @click="appFilter = 'rejected'">已拒绝</span>
            <span class="chip" :class="{ on: appFilter === '' }" @click="appFilter = ''">全部</span>
          </div>
          <input v-model="appSearch" placeholder="🔍 搜索姓名或阶段..." style="width:100%;padding:8px 12px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;margin-bottom:12px;background:#fff;" />
          <div v-if="loadingApps">加载中...</div>
          <div v-else-if="filteredApps.length === 0" class="empty">暂无申请</div>
          <div v-for="a in filteredApps" :key="a.id" class="app-card">
            <div class="app-row">
              <strong>{{ a.userId }}</strong>
              <span class="app-stage">{{ a.certLevel }} · {{ a.certStage }}</span>
              <span class="app-badge" :class="'badge-' + a.status">{{ statusLabel(a.status) }}</span>
            </div>
            <div class="app-msg" v-if="a.message">💬 {{ a.message }}</div>
            <div class="app-meta">{{ a.createdAt?.slice(0, 10) }}</div>
            <div v-if="a.status === 'pending'" class="app-actions">
              <button class="btn btn-approve" @click="doApprove(a.id)">✅ 通过</button>
              <button class="btn btn-reject" @click="doReject(a.id)">❌ 拒绝</button>
            </div>
          </div>
        </section>

        <!-- ═══════════════ Leaders ═══════════════ -->
        <section v-if="tab === 'leaders'">
          <h2>🌟 带领人管理</h2>
          <input v-model="leaderSearch" placeholder="🔍 搜索姓名..." style="width:100%;padding:8px 12px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;margin-bottom:12px;background:#fff;" />
          <div v-if="loadingLeaders">加载中...</div>
          <div v-else-if="filteredLeaders.length === 0" class="empty">暂无已通过带领人</div>
          <div v-for="l in filteredLeaders" :key="l.id" class="app-card">
            <div class="app-row">
              <strong>{{ l.userId }}</strong>
              <span class="app-stage">{{ l.certLevel }} · {{ l.certStage }}</span>
            </div>
            <div class="app-actions" style="margin-top:8px;">
              <button class="btn btn-reject" @click="doRevoke(l.id)">撤销资格</button>
            </div>
          </div>
        </section>

        <!-- ═══════════════ Recruit Config ═══════════════ -->
        <section v-if="tab === 'recruit'">
          <h2>📣 招募配置</h2>
          <div class="config-card">
            <label class="switch-row">
              <span>招募开关</span>
              <input type="checkbox" v-model="recruitActive" @change="autoSaveRecruit = false" />
            </label>
            <div class="form-group">
              <label>招募文案</label>
              <textarea v-model="recruitContent" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>截止日期</label>
              <input type="date" v-model="recruitDeadline" />
            </div>
            <button class="btn btn-approve" @click="saveRecruit" :disabled="savingRecruit">
              {{ savingRecruit ? '保存中...' : '💾 保存配置' }}
            </button>
          </div>
        </section>

        <!-- ═══════════════ Window Config ═══════════════ -->
        <section v-if="tab === 'window'">
          <h2>🪟 窗口配置</h2>
          <div class="config-card">
            <p style="font-size:12px;color:#8b7e6a;margin-bottom:12px;">开启后可自定义撮合窗口期，覆盖默认的每月最后5天规则。</p>
            <label class="switch-row">
              <span>撮合窗口开关</span>
              <input type="checkbox" v-model="windowActive" />
            </label>
            <div class="form-group">
              <label>开始日期</label>
              <input type="date" v-model="windowStart" />
            </div>
            <div class="form-group">
              <label>结束日期</label>
              <input type="date" v-model="windowEnd" />
            </div>
            <button class="btn btn-approve" @click="saveWindow" :disabled="savingWindow">
              {{ savingWindow ? '保存中...' : '💾 保存配置' }}
            </button>
          </div>
        </section>

        <!-- ═══════════════ Users ═══════════════ -->
        <section v-if="tab === 'users'">
          <h2>👤 用户管理</h2>
          <input v-model="userSearch" placeholder="🔍 搜索姓名或微信..." style="width:100%;padding:8px 12px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;margin-bottom:12px;background:#fff;" />
          <div v-if="loadingUsers">加载中...</div>
          <div v-else>
            <div class="user-card" v-for="u in filteredUsers" :key="u.name">
              <div class="app-row">
                <strong>{{ u.name }}</strong>
                <span class="app-stage">{{ u.role === 'admin' ? '👑 管理员' : '🎓 教练' }}</span>
                <span class="app-stage">{{ u.stage }}</span>
              </div>
              <div class="app-meta">微信: {{ u.wechat }} | 注册: {{ u.createdAt?.slice(0, 10) }}</div>
              <button class="btn btn-reject" style="margin-top:4px;" @click="doResetPwd(u.name)">🔑 重置密码</button>
            </div>
          </div>
        </section>

        <!-- ═══════════════ Groups ═══════════════ -->
        <section v-if="tab === 'groups'">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
            <h2 style="margin-bottom:0;">📋 小组管理</h2>
            <button class="btn btn-approve" @click="exportCSV" :disabled="filteredAdminGroups.length === 0">📥 导出CSV</button>
          </div>
          <div class="filter-row">
            <span class="chip" :class="{ on: groupStatusFilter === '' }" @click="groupStatusFilter = ''">全部</span>
            <span class="chip" :class="{ on: groupStatusFilter === 'recruiting' }" @click="groupStatusFilter = 'recruiting'">招募中</span>
            <span class="chip" :class="{ on: groupStatusFilter === 'active' }" @click="groupStatusFilter = 'active'">进行中</span>
            <span class="chip" :class="{ on: groupStatusFilter === 'completed' }" @click="groupStatusFilter = 'completed'">已完成</span>
            <span class="chip" :class="{ on: groupStatusFilter === 'disbanded' }" @click="groupStatusFilter = 'disbanded'">已解散</span>
          </div>
          <input v-model="groupSearch" placeholder="🔍 搜索组长或阶段..." style="width:100%;padding:8px 12px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;margin-bottom:12px;background:#fff;" />
          <div v-if="loadingGroups">加载中...</div>
          <div v-else-if="filteredAdminGroups.length === 0" class="empty">暂无小组</div>
          <div v-for="g in filteredAdminGroups" :key="g.id" class="app-card">
            <div class="app-row">
              <strong>{{ g.leader?.name }}的小组</strong>
              <span class="app-stage">{{ g.certLevel }} · {{ g.certStage }}</span>
              <span class="app-badge" :class="'badge-' + (g.status === 'completed' ? 'approved' : g.status === 'disbanded' ? 'rejected' : g.status === 'active' ? 'approved' : 'pending')">{{ groupStatusLabel(g.status) }}</span>
            </div>
            <div class="app-meta">
              {{ g.startDate }} ~ {{ g.endDate }} | {{ g.currentMembers }}/{{ g.maxMembers }}人 | {{ g.timeSlot }} | {{ g.durationWeeks }}周
            </div>
            <div class="app-meta" v-if="g.rhythmDesc">节奏: {{ g.rhythmDesc }}</div>
            <div class="app-msg" v-if="g.feedback">💬 组长反馈：{{ g.feedback }}</div>
            <div class="app-meta" v-if="g.completedAt">完成时间: {{ g.completedAt?.slice(0, 10) }}</div>
            <div class="app-meta" v-if="g.disbandedAt">解散时间: {{ g.disbandedAt?.slice(0, 10) }}</div>
            <div class="app-actions">
              <button class="btn btn-reject" @click="doAdminDeleteGroup(g.id, g.leader?.name)">🗑 删除小组</button>
            </div>
          </div>
        </section>
        <!-- ═══════════════ Checklist Export ═══════════════ -->
        <section v-if="tab === 'checklist'">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
            <h2 style="margin-bottom:0;">📋 组员清单</h2>
            <button class="btn btn-approve" @click="exportChecklistCSV">📥 导出CSV</button>
          </div>

          <!-- Cert stage filter -->
          <div class="filter-row" style="margin-bottom:16px;">
            <span class="chip" :class="{ on: !checklistStageFilter }" @click="checklistStageFilter = ''; loadChecklistStatus()">全部</span>
            <span
              v-for="s in checklistStages"
              :key="s"
              class="chip"
              :class="{ on: checklistStageFilter === s }"
              @click="checklistStageFilter = s; loadChecklistStatus()"
            >{{ s }}</span>
          </div>

          <!-- Search -->
          <input
            v-model="checklistSearch"
            placeholder="🔍 搜索姓名或小组..."
            style="width:100%;padding:8px 12px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;margin-bottom:12px;background:#fff;"
          />

          <div v-if="loadingChecklist">加载中...</div>
          <div v-else-if="filteredChecklistGroups.length === 0" class="empty">暂无清单数据</div>

          <div v-for="g in filteredChecklistGroups" :key="g.groupId" class="app-card" style="padding:0;overflow:hidden;">
            <!-- Group header -->
            <div
              style="display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:#fafaf8;cursor:pointer;user-select:none;"
              @click="toggleChecklistGroup(g.groupId)"
            >
              <div>
                <strong style="font-size:14px;">{{ g.groupName }}</strong>
                <span class="app-stage" style="margin-left:8px;">{{ g.certStage }}</span>
                <span class="app-stage" style="margin-left:4px;">{{ g.cohort }}期</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <button v-if="!g.confirmSessionId" class="btn-small-outline" @click.stop="generateConfirmChecklist(g.groupId)" title="为该小组生成 D28 确认清单（用于测试）">生成D28</button>
                <span style="font-size:12px;color:#8b7e6a;">{{ g.members.length }}人</span>
                <span style="font-size:12px;">{{ expandedGroups.has(g.groupId) ? '▼' : '▶' }}</span>
              </div>
            </div>

            <!-- Members table -->
            <div v-show="expandedGroups.has(g.groupId)">
              <table style="width:100%;border-collapse:collapse;font-size:12px;">
                <thead>
                  <tr style="background:#f5f0e8;text-align:left;">
                    <th style="padding:8px 12px;">姓名</th>
                    <th style="padding:8px 12px;">问卷(D1)</th>
                    <th style="padding:8px 12px;">确认(D28)</th>
                    <th style="padding:8px 12px;">缺口</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="m in g.members"
                    :key="m.userName"
                    style="border-bottom:1px solid #f0e8dd;cursor:pointer;"
                    :style="expandedMembers.has(g.groupId + '|' + m.userName) ? {background:'#fefcf9'} : {}"
                    @click="toggleChecklistMember(g.groupId, m.userName)"
                  >
                    <td style="padding:8px 12px;font-weight:600;">{{ m.userName }}</td>
                    <td style="padding:8px 12px;">
                      <span v-if="m.survey.submitted" style="color:#3d7a5a;">已提交({{ m.survey.gapCount }}项缺口)</span>
                      <span v-else style="color:#d4786e;">未提交</span>
                    </td>
                    <td style="padding:8px 12px;">
                      <span v-if="m.confirm.submitted" style="color:#3d7a5a;">已提交({{ m.confirm.completedCount }}项)</span>
                      <span v-else style="color:#b8a99a;">-</span>
                    </td>
                    <td style="padding:8px 12px;">
                      <span v-if="m.survey.submitted && m.confirm.submitted">
                        <span v-if="m.gapsRemaining === 0" style="color:#3d7a5a;">✅ 全部补齐</span>
                        <span v-else style="color:#d4786e;">⚠️ 剩{{ m.gapsRemaining }}项</span>
                      </span>
                      <span v-else style="color:#b8a99a;">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Expanded member detail -->
              <div
                v-for="m in g.members"
                :key="'detail-' + m.userName"
                v-show="expandedMembers.has(g.groupId + '|' + m.userName)"
                style="padding:8px 16px 14px;font-size:12px;border-bottom:1px solid #f0e8dd;background:#fefcf9;"
              >
                <div style="font-weight:600;margin-bottom:6px;">{{ m.userName }} · 详情</div>
                <!-- D1 gaps -->
                <div v-if="m.survey.submitted && m.survey.items" style="margin-bottom:8px;">
                  <div style="color:#8b7e6a;margin-bottom:4px;">D1 缺口项（勾选 = 还没做）：</div>
                  <div v-for="it in m.survey.items.filter((x:any) => x.completed)" :key="'s-'+it.key" style="padding:2px 8px;color:#e8894f;">
                    ⚠️ {{ getItemLabel(g, it.key) }}
                  </div>
                  <div v-if="m.survey.items.filter((x:any) => x.completed).length === 0" style="color:#b8a99a;padding:2px 8px;">无缺口</div>
                </div>
                <!-- D28 status -->
                <div v-if="m.confirm.submitted && m.survey.submitted && m.survey.items">
                  <div style="color:#8b7e6a;margin-bottom:4px;">D28 补齐情况：</div>
                  <template v-for="it in m.survey.items.filter((x:any) => x.completed)" :key="'c-'+it.key">
                    <div style="padding:2px 8px;" :style="{color: getConfirmStatus(m, it.key) ? '#3d7a5a' : '#d4786e'}">
                      {{ getConfirmStatus(m, it.key) ? '✅' : '❌' }} {{ getItemLabel(g, it.key) }}
                    </div>
                  </template>
                </div>
                <div v-if="!m.confirm.submitted" style="color:#b8a99a;">D28 尚未提交</div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  fetchApplications, approveApplication, rejectApplication,
  fetchLeaders, revokeLeader,
  updateRecruitConfig, fetchWindowConfig, updateWindowConfig,
  fetchQualifiedLeaders, addQualifiedLeaders, removeQualifiedLeader, migrateQualifiedLeaders
} from "../services/admin-api";
import { fetchRecruitConfig, fetchAdminCertGroups, fetchChecklistAdminStatus, adminDeleteCertGroup, adminGenerateConfirmChecklist } from "../services/cert-api";

const router = useRouter();
const userName = ref("");

// Tabs
const tab = ref("applications");

// Toast
const toastMsg = ref("");

// Applications
const appFilter = ref("pending");
const appSearch = ref("");
const loadingApps = ref(false);
const applications = ref<any[]>([]);
const filteredApps = computed(() => {
  let list = applications.value;
  if (appFilter.value) list = list.filter((a: any) => a.status === appFilter.value);
  if (appSearch.value) {
    const q = appSearch.value.toLowerCase();
    list = list.filter((a: any) => a.userId?.toLowerCase().includes(q) || a.certStage?.toLowerCase().includes(q));
  }
  return list;
});

async function loadApps() {
  loadingApps.value = true;
  try {
    const r = await fetchApplications();
    applications.value = r.applications || [];
  } catch { toastMsg.value = "加载申请列表失败"; }
  loadingApps.value = false;
}

async function doApprove(id: number) {
  try {
    const r = await approveApplication(id);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ 已通过";
    await loadApps();
  } catch { toastMsg.value = "操作失败"; }
}

async function doReject(id: number) {
  try {
    const r = await rejectApplication(id);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "已拒绝";
    await loadApps();
  } catch { toastMsg.value = "操作失败"; }
}

// Qualified Leaders Whitelist
const wlOpen = ref(false);
const wlInput = ref("");
const wlStage = ref("");
const wlStages = ref<string[]>([]);
const qualifiedLeaders = ref<Array<{ miniUserId: number; certStage: string; addedBy: string; addedAt: string }>>([]);
const wlGrouped = computed(() => {
  const groups: Record<string, typeof qualifiedLeaders.value> = {};
  for (const q of qualifiedLeaders.value) {
    const key = q.certStage || "未分类";
    if (!groups[key]) groups[key] = [];
    groups[key].push(q);
  }
  return groups;
});

async function loadQualifiedLeaders() {
  try {
    const r = await fetchQualifiedLeaders();
    qualifiedLeaders.value = r.qualifiedLeaders || [];
    if (r.certStages && r.certStages.length > 0) {
      wlStages.value = r.certStages;
    }
  } catch { /* silent */ }
}

async function doAddQualified() {
  const raw = wlInput.value.trim();
  if (!raw || !wlStage.value) return;
  const ids = raw.split(/[\n,，\s]+/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
  if (ids.length === 0) { toastMsg.value = "未找到有效的学员ID"; return; }
  try {
    const r = await addQualifiedLeaders(ids, wlStage.value);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = `✅ 新增 ${r.added?.length || 0} 人` + (r.skipped?.length ? `，${r.skipped.length} 人已存在` : "");
    wlInput.value = "";
    await loadQualifiedLeaders();
  } catch { toastMsg.value = "添加失败"; }
}

async function doRemoveQualified(miniUserId: number, certStage: string) {
  try {
    const r = await removeQualifiedLeader(miniUserId, certStage);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "已移除";
    await loadQualifiedLeaders();
  } catch { toastMsg.value = "移除失败"; }
}

async function doMigrateQualified() {
  if (!confirm("将旧白名单数据（无模块分类）迁移到「微习惯设计深化」？")) return;
  try {
    const r = await migrateQualifiedLeaders("微习惯设计深化");
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = `✅ 已迁移 ${r.migrated || 0} 条数据`;
    await loadQualifiedLeaders();
  } catch { toastMsg.value = "迁移失败"; }
}

// Leaders
const loadingLeaders = ref(false);
const leaderSearch = ref("");
const leaders = ref<any[]>([]);
const filteredLeaders = computed(() => {
  if (!leaderSearch.value) return leaders.value;
  const q = leaderSearch.value.toLowerCase();
  return leaders.value.filter((l: any) => l.userId?.toLowerCase().includes(q));
});

async function loadLeaders() {
  loadingLeaders.value = true;
  try {
    const r = await fetchLeaders();
    leaders.value = r.leaders || [];
  } catch { toastMsg.value = "加载带领人列表失败"; }
  loadingLeaders.value = false;
}

async function doRevoke(id: number) {
  if (!confirm("确定要撤销该带领人资格吗？")) return;
  try {
    const r = await revokeLeader(id);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "已撤销";
    await loadLeaders();
  } catch { toastMsg.value = "操作失败"; }
}

// Recruit
const recruitActive = ref(false);
const recruitContent = ref("");
const recruitDeadline = ref("");
const savingRecruit = ref(false);
const autoSaveRecruit = ref(true);

async function loadRecruit() {
  try {
    const r = await fetchRecruitConfig();
    recruitActive.value = r.isActive ?? false;
    recruitContent.value = r.content ?? "";
    recruitDeadline.value = r.deadline ?? "";
  } catch { toastMsg.value = "加载招募配置失败"; }
}

async function saveRecruit() {
  savingRecruit.value = true;
  try {
    const r = await updateRecruitConfig({
      isActive: recruitActive.value,
      content: recruitContent.value,
      deadline: recruitDeadline.value || null
    });
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ 配置已保存";
  } catch { toastMsg.value = "保存失败"; }
  savingRecruit.value = false;
}

// Window Config
const windowActive = ref(false);
const windowStart = ref("");
const windowEnd = ref("");
const savingWindow = ref(false);

async function loadWindow() {
  try {
    const r = await fetchWindowConfig();
    windowActive.value = r.isActive ?? false;
    windowStart.value = r.startDate ?? "";
    windowEnd.value = r.endDate ?? "";
  } catch { toastMsg.value = "加载窗口配置失败"; }
}

async function saveWindow() {
  savingWindow.value = true;
  try {
    const r = await updateWindowConfig({
      isActive: windowActive.value,
      startDate: windowStart.value || null,
      endDate: windowEnd.value || null
    });
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ 窗口配置已保存";
  } catch { toastMsg.value = "保存失败"; }
  savingWindow.value = false;
}

// Users
const loadingUsers = ref(false);
const userSearch = ref("");
const users = ref<any[]>([]);
const filteredUsers = computed(() => {
  if (!userSearch.value) return users.value;
  const q = userSearch.value.toLowerCase();
  return users.value.filter((u: any) => u.name?.toLowerCase().includes(q) || u.wechat?.toLowerCase().includes(q));
});

async function loadUsers() {
  loadingUsers.value = true;
  try {
    const r = await (await fetch("/api/admin/users", {
      headers: { Authorization: `Bearer ${localStorage.getItem("rc_token") ?? ""}` }
    })).json();
    users.value = r.users || [];
  } catch { toastMsg.value = "加载用户列表失败"; }
  loadingUsers.value = false;
}

async function doResetPwd(name: string) {
  if (!confirm(`确定要重置用户「${name}」的密码吗？`)) return;
  try {
    const r = await (await fetch(`/api/admin/users/${encodeURIComponent(name)}/reset-password`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${localStorage.getItem("rc_token") ?? ""}` }
    })).json();
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ 密码已重置为 111111";
  } catch { toastMsg.value = "操作失败"; }
}

// Groups
const loadingGroups = ref(false);
const adminGroups = ref<any[]>([]);
const groupStatusFilter = ref("");
const groupSearch = ref("");

const filteredAdminGroups = computed(() => {
  let list = adminGroups.value;
  if (groupStatusFilter.value) list = list.filter((g: any) => g.status === groupStatusFilter.value);
  if (groupSearch.value) {
    const q = groupSearch.value.toLowerCase();
    list = list.filter((g: any) =>
      g.leader?.name?.toLowerCase().includes(q) ||
      g.certStage?.toLowerCase().includes(q)
    );
  }
  return list;
});

function groupStatusLabel(s: string) {
  if (s === "recruiting") return "招募中";
  if (s === "active") return "进行中";
  if (s === "completed") return "已完成";
  if (s === "disbanded") return "已解散";
  return s;
}

async function loadGroups() {
  loadingGroups.value = true;
  try {
    const r = await fetchAdminCertGroups();
    adminGroups.value = r.groups || [];
  } catch { toastMsg.value = "加载小组列表失败"; }
  loadingGroups.value = false;
}

async function doAdminDeleteGroup(id: number, leaderName: string) {
  if (!confirm(`确定要删除「${leaderName || "未知"}」的小组吗？此操作不可撤销，所有成员记录也会一并删除。`)) return;
  try {
    const r = await adminDeleteCertGroup(id);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ 已删除";
    await loadGroups();
    // Also refresh checklist if that tab has data
    if (checklistGroups.value.length > 0) await loadChecklistStatus();
  } catch { toastMsg.value = "删除失败"; }
}

function exportCSV() {
  const rows: string[] = ['姓名,角色,认证阶段,开始日期,完成时间'];
  for (const g of filteredAdminGroups.value) {
    const startDate = g.startDate ?? '';
    const completedAt = g.completedAt?.slice(0, 10) ?? '';
    // 组长
    rows.push(`${g.leader?.name ?? ''},组长,${g.certStage},${startDate},${completedAt}`);
    // 组员
    if (g.members) {
      for (const m of g.members) {
        if (m.role !== 'leader') {
          rows.push(`${m.name},组员,${g.certStage},${startDate},${completedAt}`);
        }
      }
    }
  }
  const csv = '﻿' + rows.join('\n'); // BOM for Excel Chinese support
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `小组导出_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toastMsg.value = `已导出 ${rows.length - 1} 条记录`;
}

function exportChecklistCSV() {
  const token = localStorage.getItem("rc_token") ?? "";
  fetch("/api/checklist/admin/export", {
    headers: { Authorization: `Bearer ${token}` }
  }).then(res => {
    if (!res.ok) throw new Error("Export failed");
    return res.blob();
  }).then(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `清单数据导出_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toastMsg.value = "✅ 导出完成";
  }).catch(() => {
    toastMsg.value = "导出失败";
  });
}

// ═══════════════ Checklist Display ═══════════════
const checklistStages = ref<string[]>([]);
const checklistStageFilter = ref("");
const checklistSearch = ref("");
const loadingChecklist = ref(false);
const checklistGroups = ref<any[]>([]);
const expandedGroups = ref(new Set<number>());
const expandedMembers = ref(new Set<string>());

const filteredChecklistGroups = computed(() => {
  let list = checklistGroups.value;
  if (checklistSearch.value) {
    const q = checklistSearch.value.toLowerCase();
    list = list.filter((g: any) =>
      g.groupName?.toLowerCase().includes(q) ||
      g.leaderName?.toLowerCase().includes(q) ||
      g.members?.some((m: any) => m.userName?.toLowerCase().includes(q))
    );
  }
  return list;
});

async function loadChecklistStatus() {
  loadingChecklist.value = true;
  try {
    const r = await fetchChecklistAdminStatus(checklistStageFilter.value || undefined);
    checklistStages.value = r.certStages || [];
    checklistGroups.value = r.groups || [];
    // Auto-expand groups that have data
    for (const g of r.groups || []) {
      if (g.members?.length > 0) expandedGroups.value.add(g.groupId);
    }
  } catch { toastMsg.value = "加载清单状态失败"; }
  loadingChecklist.value = false;
}

async function generateConfirmChecklist(groupId: number) {
  try {
    const r = await adminGenerateConfirmChecklist(groupId);
    if (r.error) { toastMsg.value = r.error; return; }
    toastMsg.value = "✅ D28 确认清单已生成，刷新查看";
    await loadChecklistStatus();
  } catch { toastMsg.value = "生成确认清单失败"; }
}

function toggleChecklistGroup(groupId: number) {
  if (expandedGroups.value.has(groupId)) {
    expandedGroups.value.delete(groupId);
  } else {
    expandedGroups.value.add(groupId);
  }
}

function toggleChecklistMember(groupId: number, userName: string) {
  const key = groupId + '|' + userName;
  if (expandedMembers.value.has(key)) {
    expandedMembers.value.delete(key);
  } else {
    expandedMembers.value.add(key);
  }
}

function getItemLabel(group: any, key: string): string {
  return group.itemLabels?.[key] || key;
}

function getConfirmStatus(member: any, key: string): boolean {
  if (!member.confirm?.items) return false;
  const item = member.confirm.items.find((it: any) => it.key === key);
  return item?.completed ?? false;
}

// Utility
function statusLabel(s: string) {
  return s === 'pending' ? '待审核' : s === 'approved' ? '已通过' : '已拒绝';
}

function goHome() { router.push("/"); }

onMounted(async () => {
  const token = localStorage.getItem("rc_token");
  if (!token) { router.push("/login"); return; }
  try {
    const res = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    if (!data.success || data.user?.role !== "admin") {
      toastMsg.value = "需要管理员权限";
      setTimeout(() => router.push("/"), 1500);
      return;
    }
    userName.value = data.user.name;
  } catch { router.push("/login"); }

  loadApps();
  loadLeaders();
  loadRecruit();
  loadWindow();
  loadUsers();
  loadGroups();
  loadQualifiedLeaders();
});
</script>

<style>
.phone-frame {
  max-width: 430px;
  margin: 0 auto;
  height: 100%;
  background: #fef9f2;
  box-shadow: 0 0 40px rgba(0,0,0,0.08);
  position: relative;
}

.admin-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  color: #3d3529;
  background: #f5f0e8;
  -webkit-font-smoothing: antialiased;
}

/* Toast */
.admin-toast {
  position: fixed; top: 12px; left: 50%; transform: translateX(-50%); z-index: 99;
  background: #333; color: #fff; padding: 10px 20px; border-radius: 10px; font-size: 13px;
  opacity: 0; pointer-events: none; transition: opacity 0.3s;
}
.admin-toast.show { opacity: 1; pointer-events: auto; }

/* Top bar */
.admin-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; background: #3d3529; color: #fff;
}
.atb-logo { font-size: 16px; font-weight: 700; }
.atb-right { display: flex; align-items: center; gap: 12px; }
.atb-user { font-size: 13px; opacity: 0.8; }
.atb-logout { padding: 6px 14px; border: 1px solid rgba(255,255,255,0.3); border-radius: 6px; background: none; color: #fff; cursor: pointer; font-size: 13px; }

/* Body */
.admin-body { display: flex; flex: 1; overflow: hidden; }

/* Sidebar */
.admin-sidebar {
  width: 160px; background: #fff; border-right: 1px solid #f0e8dd; padding: 12px 0;
  display: flex; flex-direction: column; gap: 2px; flex-shrink: 0;
}
.admin-sidebar button {
  display: block; width: 100%; text-align: left; padding: 12px 16px;
  border: none; background: none; font-size: 13px; color: #5c5040; cursor: pointer;
  transition: all 0.15s; font-family: inherit; border-right: 3px solid transparent;
}
.admin-sidebar button:hover { background: #fef9f2; color: #e8894f; }
.admin-sidebar button.active { background: #fef9f2; color: #e8894f; border-right-color: #e8894f; font-weight: 600; }

/* Main */
.admin-main { flex: 1; overflow-y: auto; padding: 20px 24px; }
.admin-main h2 { font-size: 18px; margin-bottom: 16px; }

/* Filter */
.filter-row { display: flex; gap: 8px; margin-bottom: 16px; }

/* Cards */
.app-card { background: #fff; border-radius: 12px; padding: 14px 16px; margin-bottom: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.app-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.app-stage { font-size: 12px; color: #8b7e6a; background: #f0ede9; padding: 2px 8px; border-radius: 8px; }
.app-badge { font-size: 11px; padding: 2px 8px; border-radius: 8px; }
.badge-pending { background: #fef5ee; color: #e8894f; }
.badge-approved { background: #edf6f1; color: #3d7a5a; }
.badge-rejected { background: #fdf3f1; color: #d4786e; }
.app-msg { font-size: 13px; color: #5c5040; margin-top: 6px; }
.app-meta { font-size: 11px; color: #b8a99a; margin-top: 4px; }
.app-actions { display: flex; gap: 8px; margin-top: 6px; }

/* Buttons */
.btn { padding: 6px 16px; border-radius: 8px; border: none; font-size: 13px; cursor: pointer; font-family: inherit; font-weight: 600; }
.btn-approve { background: #edf6f1; color: #3d7a5a; }
.btn-reject { background: #fdf3f1; color: #d4786e; }

/* Chips */
.chip { padding: 5px 14px; border: 1px solid #e8e4df; border-radius: 14px; font-size: 12px; cursor: pointer; white-space: nowrap; }
.chip.on { background: #e8894f; color: #fff; border-color: #e8894f; }

/* Config */
.config-card { background: #fff; border-radius: 12px; padding: 20px; }
.switch-row { display: flex; justify-content: space-between; align-items: center; font-weight: 600; margin-bottom: 16px; }
.switch-row input[type="checkbox"] { width: 44px; height: 24px; accent-color: #e8894f; transform: scale(1.2); }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 12px; color: #8b7e6a; margin-bottom: 6px; font-weight: 600; }
.form-group textarea, .form-group input[type="date"] {
  width: 100%; padding: 10px; border: 1px solid #e8e4df; border-radius: 8px; font-size: 13px;
  font-family: inherit; background: #fafaf8;
}

/* User card */
.user-card { background: #fff; border-radius: 12px; padding: 14px 16px; margin-bottom: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }

/* Empty */
.empty { text-align: center; padding: 40px; color: #b8a99a; font-size: 14px; }

@media (max-width: 600px) {
  .admin-sidebar { width: 100px; }
  .admin-sidebar button { padding: 10px 8px; font-size: 11px; }
  .admin-main { padding: 16px 12px; }
}
.btn-small-outline { padding: 2px 8px; border-radius: 4px; border: 1px solid #c0835a; background: #fff; color: #c0835a; font-size: 11px; cursor: pointer; font-family: inherit; white-space: nowrap; }
.btn-small-outline:hover { background: #fdf6f0; }

/* Whitelist panel */
.wl-toggle { padding: 8px 12px; background: #f8f4ee; border-radius: 8px; cursor: pointer; font-size: 13px; margin-bottom: 8px; user-select: none; display: flex; justify-content: space-between; align-items: center; }
.wl-toggle:hover { background: #f0ebe0; }
.wl-panel { background: #fff; border: 1px solid #e8e4df; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.wl-hint { font-size: 11px; color: #999; margin-bottom: 8px; }
.wl-row { display: flex; gap: 8px; margin-bottom: 8px; }
.wl-select { padding: 6px 10px; border: 1px solid #e8e4df; border-radius: 6px; font-size: 13px; background: #fff; font-family: inherit; }
.wl-textarea { width: 100%; padding: 8px; border: 1px solid #e8e4df; border-radius: 6px; font-size: 12px; font-family: monospace; resize: vertical; box-sizing: border-box; }
.wl-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.wl-chip { display: inline-flex; align-items: center; gap: 2px; padding: 2px 8px; background: #eef7e2; border: 1px solid #c8e0b0; border-radius: 12px; font-size: 11px; color: #5a7a3a; }
.wl-remove { border: none; background: none; color: #c55; cursor: pointer; font-size: 14px; padding: 0 2px; line-height: 1; }
.wl-remove:hover { color: #a00; }
</style>
